'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { AlertCircle, Clock, CheckCircle2, XCircle, Plus, Search, MessageSquare, User, ChevronRight, Zap,  } from 'lucide-react';

interface Ticket {
  id: string;
  title: string;
  client: string;
  assignee: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'in-progress' | 'pending' | 'resolved' | 'closed';
  category: string;
  created: string;
  updated: string;
  replies: number;
}

const tickets: Ticket[] = [
  { id: 'TK-1042', title: 'VOIP lines intermittently dropping calls during peak hours', client: 'Meridian BPO', assignee: 'Layla K.', priority: 'critical', status: 'in-progress', category: 'VOIP', created: '2h ago', updated: '30m ago', replies: 6 },
  { id: 'TK-1041', title: 'Invoice #INV-0089 shows incorrect service charges', client: 'CloudBridge Inc.', assignee: 'Omar S.', priority: 'high', status: 'open', category: 'Billing', created: '4h ago', updated: '4h ago', replies: 2 },
  { id: 'TK-1040', title: 'Digital marketing dashboard not loading campaign data', client: 'Vantage Media', assignee: 'Ryan A.', priority: 'high', status: 'pending', category: 'Digital Marketing', created: '1d ago', updated: '3h ago', replies: 4 },
  { id: 'TK-1039', title: 'Request to add 3 new BPO agents to existing contract', client: 'Apex Digital', assignee: 'Layla K.', priority: 'medium', status: 'in-progress', category: 'BPO', created: '2d ago', updated: '1d ago', replies: 8 },
  { id: 'TK-1038', title: 'Website contact form not sending email notifications', client: 'Gulf Retail Group', assignee: 'Ryan A.', priority: 'medium', status: 'open', category: 'Web Dev', created: '3d ago', updated: '2d ago', replies: 3 },
  { id: 'TK-1037', title: 'Monthly performance report missing last week data', client: 'Pinnacle Logistics', assignee: 'Omar S.', priority: 'low', status: 'resolved', category: 'Reporting', created: '5d ago', updated: '1d ago', replies: 5 },
  { id: 'TK-1036', title: 'SSL certificate renewal required for client subdomain', client: 'NovaTech Solutions', assignee: 'Ryan A.', priority: 'high', status: 'resolved', category: 'Web Dev', created: '6d ago', updated: '2d ago', replies: 7 },
  { id: 'TK-1035', title: 'Onboarding documentation not accessible via client portal', client: 'Gulf Retail Group', assignee: 'Layla K.', priority: 'low', status: 'closed', category: 'General', created: '1w ago', updated: '3d ago', replies: 9 },
];

const priorityConfig = {
  critical: { label: 'Critical', color: 'text-danger',  bg: 'bg-danger/10',  dot: 'bg-danger' },
  high:     { label: 'High',     color: 'text-orange-400', bg: 'bg-orange-400/10', dot: 'bg-orange-400' },
  medium:   { label: 'Medium',   color: 'text-warning', bg: 'bg-warning/10', dot: 'bg-warning' },
  low:      { label: 'Low',      color: 'text-muted-foreground', bg: 'bg-muted', dot: 'bg-muted-foreground' },
};

const statusConfig = {
  'open':        { label: 'Open',        color: 'text-primary',  bg: 'bg-primary/10',  icon: AlertCircle },
  'in-progress': { label: 'In Progress', color: 'text-warning',  bg: 'bg-warning/10',  icon: Clock },
  'pending':     { label: 'Pending',     color: 'text-amber-400', bg: 'bg-amber-400/10', icon: Clock },
  'resolved':    { label: 'Resolved',    color: 'text-success',  bg: 'bg-success/10',  icon: CheckCircle2 },
  'closed':      { label: 'Closed',      color: 'text-muted-foreground', bg: 'bg-muted', icon: XCircle },
};

export default function SupportTicketsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const filtered = tickets.filter((t) => {
    const matchSearch = t.title.toLowerCase().includes(search.toLowerCase()) || t.client.toLowerCase().includes(search.toLowerCase()) || t.id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    return matchSearch && matchStatus && matchPriority;
  });

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in-progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
  const criticalCount = tickets.filter(t => t.priority === 'critical').length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Support Tickets</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Track, prioritize, and resolve client support requests</p>
          </div>
          <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex-shrink-0">
            <Plus size={16} />
            <span className="hidden sm:inline">New Ticket</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: 'Open Tickets', value: openCount, sub: 'Awaiting assignment', icon: AlertCircle, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'In Progress', value: inProgressCount, sub: 'Actively being worked', icon: Clock, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Critical Issues', value: criticalCount, sub: 'Needs immediate action', icon: Zap, color: 'text-danger', bg: 'bg-danger/10' },
            { label: 'Resolved (7d)', value: resolvedCount, sub: 'Avg 18h resolution time', icon: CheckCircle2, color: 'text-success', bg: 'bg-success/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-3 sm:p-4 mb-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tickets by ID, title, or client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'open', 'in-progress', 'pending', 'resolved', 'closed'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {s === 'all' ? 'All Status' : s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPriorityFilter(p)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${priorityFilter === p ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {p === 'all' ? 'All Priority' : p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Ticket List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="divide-y divide-border">
            {filtered.map((ticket) => {
              const pc = priorityConfig[ticket.priority];
              const sc = statusConfig[ticket.status];
              const StatusIcon = sc.icon;
              return (
                <div key={ticket.id} className="flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4 hover:bg-muted/20 transition-colors cursor-pointer">
                  <div className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${pc.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1">
                          <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pc.bg} ${pc.color}`}>{pc.label}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{ticket.category}</span>
                        </div>
                        <p className="text-sm font-medium text-foreground line-clamp-2">{ticket.title}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><User size={11} />{ticket.client}</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><MessageSquare size={11} />{ticket.replies}</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground hidden sm:flex"><Clock size={11} />Updated {ticket.updated}</span>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
                        <span className={`inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          <StatusIcon size={11} />
                          <span className="hidden sm:inline">{sc.label}</span>
                        </span>
                        <span className="text-xs text-muted-foreground hidden lg:block">{ticket.assignee}</span>
                        <ChevronRight size={14} className="text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="py-16 text-center text-muted-foreground text-sm">No tickets match your filters.</div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
